/**
 * Companies Query Hooks
 * React Query hooks for company data fetching and mutations
 * Used by the Companies management tab (src/Pages/Management)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { companiesService } from 'src/api/services';
import { useToast } from 'src/Contexts/ToastContext';
import { queryKeys } from './queryKeys';
import type { Company } from 'src/types';

/**
 * Hook to fetch all companies
 *
 * @returns React Query result with companies list
 */
export function useCompaniesQuery() {
  return useQuery({
    queryKey: queryKeys.companies.lists(),
    queryFn: () => companiesService.getAll(),
    staleTime: 10 * 60 * 1000, // 10 minutes (companies don't change often)
  });
}

/**
 * Mutation hook to create a new company
 *
 * @returns React Query mutation result
 */
export function useCreateCompanyMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (data: Partial<Company>) => companiesService.create(data),

    // Optimistic update
    onMutate: async (newCompany) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.companies.all });

      const previousCompanies = queryClient.getQueryData(queryKeys.companies.lists());

      queryClient.setQueryData<Company[]>(queryKeys.companies.lists(), (old = []) => [
        ...old,
        { ...newCompany, id: `temp-${Date.now()}` } as Company,
      ]);

      return { previousCompanies };
    },

    onError: (err, _newCompany, context) => {
      if (context?.previousCompanies) {
        queryClient.setQueryData(queryKeys.companies.lists(), context.previousCompanies);
      }
      toast.error('Failed to create company');
      console.error('Create company error:', err);
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
      toast.success('Company created successfully');
    },
  });
}

/**
 * Mutation hook to update an existing company
 *
 * @returns React Query mutation result
 */
export function useUpdateCompanyMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Company> }) =>
      companiesService.update(id, data),

    // Optimistic update
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.companies.all });

      const previousCompanies = queryClient.getQueryData(queryKeys.companies.lists());

      queryClient.setQueryData<Company[]>(queryKeys.companies.lists(), (old = []) =>
        old.map((company) => (company.id === id ? { ...company, ...data } : company))
      );

      return { previousCompanies };
    },

    onError: (err, { id: _id }, context) => {
      if (context?.previousCompanies) {
        queryClient.setQueryData(queryKeys.companies.lists(), context.previousCompanies);
      }
      toast.error('Failed to update company');
      console.error('Update company error:', err);
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
      toast.success('Company updated successfully');
    },
  });
}

/**
 * Mutation hook to delete a company
 *
 * @returns React Query mutation result
 */
export function useDeleteCompanyMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (id: string) => companiesService.delete(id),

    // Optimistic update
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.companies.all });

      const previousCompanies = queryClient.getQueryData(queryKeys.companies.lists());

      queryClient.setQueryData<Company[]>(queryKeys.companies.lists(), (old = []) =>
        old.filter((company) => company.id !== id)
      );

      return { previousCompanies };
    },

    onError: (err, _id, context) => {
      if (context?.previousCompanies) {
        queryClient.setQueryData(queryKeys.companies.lists(), context.previousCompanies);
      }
      toast.error('Failed to delete company');
      console.error('Delete company error:', err);
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
      toast.success('Company deleted successfully');
    },
  });
}
